import React, { useState, ComponentType, useEffect } from 'react';

import { withFormik } from 'formik';
import {
  CHANNELS,
  DraftChannelDataType,
  initialWebHookData,
  initialWebHookDataTypes
} from 'types';
import { useDispatch, useSelector } from 'react-redux';
import {
  activateReferralChannelsStart,
  draftReferralChannelsStart,
  selectDraftChannels
} from 'redux/channels';
import { currentUser } from 'redux/session/selectors';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import WebHookChannelForm from './webHookChannel.form';
import * as Yup from 'yup';
dayjs.extend(utc);

const Schema = Yup.object().shape({
  url: Yup.string()
    .required('Web service URL is required')
    .url('Web service URL is not valid')
    .matches(/^https/, 'Web service URL is not valid'),
  clientKey: Yup.string()
    .required('Signing secret key is required')
    .min(8, 'Signing secret key is min 8 character and max 32 character length')
    .max(
      32,
      'Signing secret key is min 8 character and max 32 character length'
    ),
  gmlProjection: Yup.string().required('GIS Projection is required')
});

export const WebHookChannelFormComponent: React.FC<any> = ({
  advancedOptions,
  primaryContentOptions,
  copiedActiveValue,
  clearCopiedActiveValue,
  copiedAdvanceFeaturesValue,
  copiedPrimaryContentOption
}) => {
  const dispatch = useDispatch();
  const [organisationId, setOrganisationId] = useState<any>();
  const [channelId, setChannelId] = useState<any>();
  const [formValues, setFormValues] = useState<initialWebHookDataTypes>(
    initialWebHookData
  );
  const user: any = useSelector(currentUser);
  useEffect(() => {
    setOrganisationId(user?.organisationId || user?.id);
  }, [user]);

  useEffect(() => {
    if (copiedActiveValue) {
      const values = JSON.parse(copiedActiveValue);
      values?.configurationType === CHANNELS.WEBHOOK &&
        setFormValues({
          ...initialWebHookData,
          ...values
        });
    }
  }, [copiedActiveValue]);

  useEffect(() => {
    if (copiedAdvanceFeaturesValue) {
      const values = JSON.parse(copiedAdvanceFeaturesValue);
      advancedOptions.current = {
        maxEnquiryArea: values.maxEnquiryArea
          ? values.maxEnquiryArea / 1e6
          : undefined,
        splitEnquiries: values.splitEnquiries ? 'split' : undefined,
        referralSummaryFrequency: values.referralSummaryFrequency,
        referralSummaryEmail: values.referralSummaryEmail,
        isExpandPanel: true
      };
    }
  }, [copiedAdvanceFeaturesValue, advancedOptions]);

  useEffect(() => {
    if (copiedPrimaryContentOption) {
      primaryContentOptions.current = {
        filenameOption: copiedPrimaryContentOption.filenameOption,
        filenameValue: copiedPrimaryContentOption.filenameValue,
        webMap: copiedPrimaryContentOption.webMap,
        responseType: copiedPrimaryContentOption.responseType
      };
    }
  }, [copiedPrimaryContentOption, primaryContentOptions]);

  useEffect(() => {
    clearCopiedActiveValue();
  }, [formValues, clearCopiedActiveValue]);

  const draftChannel: any = useSelector(selectDraftChannels);
  useEffect(() => {
    if (draftChannel && draftChannel.configurationType === CHANNELS.WEBHOOK) {
      const {
        id,
        name,
        LegacyEmailConfiguration,
        WebhookConfiguration
      } = draftChannel;

      const url = WebhookConfiguration?.WebhookSubscription?.url;
      const clientKey = WebhookConfiguration?.WebhookSubscription?.clientKey;
      const customHeaders =
        WebhookConfiguration?.WebhookSubscription?.customHeaders;
      const gmlProjection = WebhookConfiguration?.gmlProjection;
      const defaultValues: initialWebHookDataTypes = {
        ...initialWebHookData,
        name: name || '',
        referralSummaryEmail: LegacyEmailConfiguration?.emailNormalEnquiries,
        gmlProjection: gmlProjection || '',
        url: url ? url : initialWebHookData.url,
        clientKey: clientKey ? clientKey : initialWebHookData.clientKey,
        customHeaders:
          customHeaders?.length > 0 ? customHeaders : [{ name: '', value: '' }]
      };
      setChannelId(id);
      setFormValues(defaultValues);
    }
  }, [draftChannel]);
  // Email Channel Sandbox Component
  const WebHookFormikComponent: ComponentType<any> = withFormik<
    DraftChannelDataType,
    initialWebHookDataTypes
  >({
    mapPropsToValues: () => ({
      name: formValues?.name,
      configurationType: formValues?.configurationType,
      referralSummaryEmail: formValues?.referralSummaryEmail,
      referralSummaryFrequency: 'NEVER',
      gmlProjection: formValues?.gmlProjection,
      url: formValues?.url,
      clientKey: formValues?.clientKey,
      customHeaders: formValues?.customHeaders
    }),
    mapPropsToTouched: () => {
      if (copiedActiveValue) {
        const values = JSON.parse(copiedActiveValue);
        if (values?.configurationType === CHANNELS.WEBHOOK) {
          return values;
        }
      }

      return {};
    },
    validationSchema: Schema,
    handleSubmit: (values: initialWebHookDataTypes, { setSubmitting }) => {
      // setSubmitting(true);
      const packReferralChannelPayload = (val: any) => {
        const {
          name,
          configurationType,
          url,
          clientKey,
          customHeaders,
          referralSummaryFrequency
        } = val;

        setFormValues(val);
        const payload: DraftChannelDataType = {
          name,
          configurationType: configurationType,
          WebhookConfiguration: {
            url,
            clientKey,
            customHeaders,
            isGmlRequired: true,
            gmlVersion: 'GMLv3',
            gmlProjection: values.gmlProjection
          },
          referralSummaryFrequency
        };

        if (advancedOptions?.current) {
          payload.maxEnquiryArea =
            advancedOptions.current?.maxEnquiryArea &&
            advancedOptions.current?.splitEnquiries
              ? advancedOptions.current.maxEnquiryArea * 1e6
              : undefined;
          payload.referralSummaryFrequency =
            advancedOptions.current?.referralSummaryFrequency;
          payload.referralSummaryEmail =
            advancedOptions.current?.referralSummaryEmail &&
            advancedOptions.current?.referralSummaryFrequency !== 'NEVER'
              ? advancedOptions.current.referralSummaryEmail
                  .split(',')
                  .join(';')
              : undefined;
        }

        payload.primaryContent = {
          filenameOption: primaryContentOptions.current?.filenameOption,
          filenameValue: primaryContentOptions.current?.filenameValue,
          webMap: primaryContentOptions.current?.webMap,
          responseType: primaryContentOptions.current?.responseType
        };

        return payload;
      };

      // make-active
      if (values.actionType === 'make-active') {
        const activeChannelPayload: DraftChannelDataType = packReferralChannelPayload(
          values
        );
        const packedActivePayload: any = {
          ...activeChannelPayload,
          status: 'LIVE'
        };
        if (organisationId && channelId) {
          dispatch(
            activateReferralChannelsStart({
              organisationId,
              channelId,
              requestBody: packedActivePayload
            })
          );
        }
      }
      // save-draft
      if (values.actionType === 'save-draft' && organisationId) {
        const name = dayjs(new Date()).utc().local().format().toString(); //TODO
        const draftPayload: DraftChannelDataType = packReferralChannelPayload(
          values
        );
        const packedDraftPayload: any = {
          ...draftPayload,
          name,
          status: 'DRAFT'
        };

        draftChannel?.WebhookConfiguration !== null && channelId
          ? dispatch(
              activateReferralChannelsStart({
                organisationId,
                channelId,
                requestBody: packedDraftPayload
              })
            )
          : dispatch(
              draftReferralChannelsStart({
                organisationId,
                requestBody: packedDraftPayload
              })
            );
      }
      setSubmitting(false);
    }
  })(WebHookChannelForm);

  return (
    <WebHookFormikComponent
      advancedOptions={advancedOptions}
      primaryContentOptions={primaryContentOptions}
    />
  );
};
